import { useQuery } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { Panel, PanelHeader, Skeleton, Stamp, ReadFailed } from '@/components/ui';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';

/**
 * What is in a bucket that no rule was written for.
 *
 * Attribution can only name software someone has already described. On a large
 * corpus that leaves most of the files in one undifferentiated pile: capway
 * reported 98% untraced, which reads as failure and tells a buyer nothing.
 *
 * This groups those files by the shape of their records - the exact set of keys
 * they carry - so the pile resolves into a handful of named shapes a person can
 * recognise and confirm. A cluster of forty thousand objects with the same four
 * keys is an event log; one carrying a vendor's hostname is that vendor's
 * export, inferred from shape alone.
 *
 * Nothing here claims a tool. Every cluster is a candidate for a human to
 * confirm, and confirming one still has to clear the evidence bar before
 * anything reads as proven. The page says so rather than letting a shape look
 * like a conclusion.
 */

const SHAPE: Record<string, string> = {
  ARRAY_OF_OBJECTS: 'a list of records',
  SINGLE_OBJECT: 'one record per file',
  JSON_LINES: 'one record per line',
};

interface Cluster {
  signatureSha256: string;
  recordShape: string;
  signatureKeys: string[];
  keysTruncated: boolean;
  objectCount: number;
  unrecognisedObjectCount: number;
  observedHosts?: string[];
}

function readClusters(value: unknown): Cluster[] | null {
  const data = (value as { data?: unknown })?.data ?? value;
  if (!data || typeof data !== 'object') return null;
  const raw = (data as Record<string, unknown>).clusters;
  if (!Array.isArray(raw)) return null;
  return (raw as Record<string, unknown>[]).map((c) => ({
    signatureSha256: String(c.signatureSha256 ?? ''),
    recordShape: String(c.recordShape ?? ''),
    signatureKeys: Array.isArray(c.signatureKeys) ? (c.signatureKeys as string[]) : [],
    keysTruncated: c.keysTruncated === true,
    objectCount: typeof c.objectCount === 'number' ? c.objectCount : 0,
    unrecognisedObjectCount: typeof c.unrecognisedObjectCount === 'number' ? c.unrecognisedObjectCount : 0,
    observedHosts: Array.isArray(c.observedHosts) ? (c.observedHosts as string[]) : undefined,
  }));
}

export function SchemaClusters({ bucketId }: { bucketId: string }) {
  const q = useQuery({
    queryKey: ['schema-clusters', bucketId],
    queryFn: ({ signal }) => backend.getSchemaClusters(bucketId, signal),
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 120_000,
  });

  const clusters = q.data ? readClusters(q.data) : null;
  const sorted = [...(clusters ?? [])].sort((a, b) => b.objectCount - a.objectCount);
  const biggest = Math.max(...sorted.map((c) => c.objectCount), 1);
  const total = sorted.reduce((n, c) => n + c.objectCount, 0);
  const untraced = sorted.reduce((n, c) => n + c.unrecognisedObjectCount, 0);

  return (
    <Panel>
      <PanelHeader
        title="What is in here that no rule describes"
        sub={
          q.isPending
            ? 'grouping files by the shape of their records'
            : sorted.length === 0
              ? 'grouped by the shape of their records'
              : `${sorted.length} distinct shapes over ${total.toLocaleString()} files · ${untraced.toLocaleString()} of them matched no rule`
        }
      />

      {q.isPending ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : q.isError ? (
        <ReadFailed
          onRetry={() => void q.refetch()}
          what={
            q.error instanceof ApiError
              ? q.error.code === 'DEPENDENCY_UNAVAILABLE'
                ? 'The backend can serve this, but the store it reads is not available for this bucket right now. Nothing is wrong with the data; this is a backend dependency.'
                : (q.error.reason ?? q.error.message)
              : 'The API did not respond.'
          }
        />
      ) : sorted.length === 0 ? (
        <p className="px-4 py-3 text-small text-ink-3">
          No record shapes were found here. Either every file was already traced to a tool, or nothing in this bucket
          holds structured records to group.
        </p>
      ) : (
        <>
          {sorted.map((c) => {
            const allUntraced = c.unrecognisedObjectCount >= c.objectCount;
            return (
              <div key={c.signatureSha256} className="border-b border-line px-4 py-3 last:border-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="mono text-row text-ink">{c.objectCount.toLocaleString()} files</span>
                  <span className="text-small text-ink-3">{SHAPE[c.recordShape] ?? say(c.recordShape)}</span>
                  {c.observedHosts?.length ? (
                    <span className="mono text-caption text-acc-2">{c.observedHosts.slice(0, 2).join(', ')}</span>
                  ) : null}
                  <Stamp tone={allUntraced ? 'warn' : 'mut'} className="ml-auto">
                    {allUntraced ? 'NO RULE MATCHES THESE' : `${c.unrecognisedObjectCount.toLocaleString()} untraced`}
                  </Stamp>
                </div>

                <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
                  <span
                    className={cn('block h-full rounded-full', allUntraced ? 'bg-line-2' : 'bg-acc')}
                    style={{ width: `${Math.max((c.objectCount / biggest) * 100, 1.5)}%` }}
                  />
                </span>

                <div className="mt-2 flex flex-wrap gap-1">
                  {c.signatureKeys.slice(0, 12).map((k) => (
                    <span key={k} className="mono rounded border border-line bg-panel-2 px-1.5 py-0.5 text-caption text-ink-2">
                      {k}
                    </span>
                  ))}
                  {(c.keysTruncated || c.signatureKeys.length > 12) && (
                    <span className="px-1 py-0.5 text-caption text-ink-4">
                      {c.keysTruncated ? 'and more keys the backend truncated' : `and ${c.signatureKeys.length - 12} more`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
            These are shapes. A shape suggests what produced it, and naming the software is a judgement a
            person makes, and it still has to clear the evidence bar before anything counts as proven.
          </p>
        </>
      )}
    </Panel>
  );
}
